import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import api, { getApiErrorMessage } from "services/api";
import { API_ENDPOINTS } from "services/apiEndPoints";
import { callToast } from "components/common/Toast";
import withRoleBasedAccess from "components/common/hoc/withRoleBasedAccess";
import { feedbackViewPermissions } from "services/permissions";
import { getProfileData } from "utils/utils";
import {
  getWorkspaceHeroSx,
  getWorkspaceSectionSx,
  workspaceCompactPageSx,
  workspaceSectionHeadingSx,
} from "components/common/workspaceStyles";

type FeedbackFormValues = {
  summary: string;
  description: string;
  priority: string;
};

type UserProfile = {
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
};

type FeedbackApiResponse = {
  success?: boolean;
  message?: string;
  issueKey?: string | null;
  issueUrl?: string | null;
};

type FeedbackSubmissionState =
  | {
      status: "success";
      message: string;
      issueKey?: string | null;
      issueUrl?: string | null;
    }
  | {
      status: "error";
      message: string;
    }
  | null;

type FeedbackFormProps = {
  variant?: "page" | "dialog";
};

const SUMMARY_MAX_LENGTH = 150;
const DESCRIPTION_MAX_LENGTH = 2000;

const validationSchema = Yup.object({
  summary: Yup.string()
    .trim()
    .required("Summary is required")
    .max(SUMMARY_MAX_LENGTH, "Summary cannot exceed 150 characters"),
  description: Yup.string()
    .trim()
    .required("Description is required")
    .max(DESCRIPTION_MAX_LENGTH, "Description cannot exceed 2000 characters"),
  priority: Yup.string()
    .oneOf(["Low", "Medium", "High"], "Priority must be Low, Medium, or High")
    .required("Priority is required"),
});

const getRequestedByName = (profile: UserProfile | null) => {
  return (
    [profile?.given_name?.trim(), profile?.family_name?.trim()].filter(Boolean).join(" ") || profile?.name?.trim() || ""
  );
};

export function FeedbackPage() {
  return <FeedbackForm variant="page" />;
}

export function FeedbackForm({ variant = "page" }: FeedbackFormProps) {
  const [submissionState, setSubmissionState] = useState<FeedbackSubmissionState>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const profile = getProfileData() as UserProfile | null;

  const formik = useFormik<FeedbackFormValues>({
    initialValues: {
      summary: "",
      description: "",
      priority: "",
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      setIsSubmittingFeedback(true);
      setSubmissionState(null);

      try {
        await api<FeedbackApiResponse>("POST", API_ENDPOINTS.FEEDBACK, {
          ...values,
          requestedByName: getRequestedByName(profile) || undefined,
          requestedBySso: profile?.preferred_username?.trim() || undefined,
        });
        const issueMessage = "Feedback submitted successfully.";

        setSubmissionState({
          status: "success",
          message: issueMessage,
        });
        callToast("success", issueMessage);
        helpers.resetForm();
      } catch (error) {
        const errorMessage = getApiErrorMessage(error, "Unable to create the Jira feedback ticket.");
        setSubmissionState({
          status: "error",
          message: errorMessage,
        });
      } finally {
        setIsSubmittingFeedback(false);
      }
    },
  });
  const shouldShowValidationMessages = formik.submitCount > 0;
  const getTextFieldHelperText = (fieldName: "summary" | "description", maxLength: number) => {
    const errorMessage = shouldShowValidationMessages ? formik.errors[fieldName] : "";
    return errorMessage || `${formik.values[fieldName].length}/${maxLength}`;
  };
  const introContent = (
    <Box>
      {variant === "page" && (
        <Typography variant="h4" sx={workspaceSectionHeadingSx}>
          Recon Feedback
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        Use this form to share product feedback, usability gaps, or enhancement ideas.{" "}
        <br />
        <Box component="strong" sx={{ fontWeight: 700 }}>
          This is not for production support, live broadcast issues, missing As-Runs, or urgent operational requests.
        </Box>
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={2.5} sx={variant === "page" ? workspaceCompactPageSx : undefined}>
      {variant === "page" ? (
        <Paper elevation={0} sx={(theme) => getWorkspaceHeroSx(theme)}>
          {introContent}
        </Paper>
      ) : (
        introContent
      )}

      <Paper elevation={0} sx={(theme) => getWorkspaceSectionSx(theme)}>
        <Stack spacing={2}>
          {submissionState && (
            <Alert severity={submissionState.status} variant="outlined" onClose={() => setSubmissionState(null)}>
              <span>{submissionState.message}</span>
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                id="summary"
                name="summary"
                label="Summary"
                value={formik.values.summary}
                onChange={formik.handleChange}
                error={shouldShowValidationMessages && Boolean(formik.errors.summary)}
                helperText={getTextFieldHelperText("summary", SUMMARY_MAX_LENGTH)}
                fullWidth
                required
                inputProps={{ maxLength: SUMMARY_MAX_LENGTH }}
              />

              <TextField
                id="description"
                name="description"
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                error={shouldShowValidationMessages && Boolean(formik.errors.description)}
                helperText={getTextFieldHelperText("description", DESCRIPTION_MAX_LENGTH)}
                fullWidth
                required
                multiline
                minRows={6}
                inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
              />

              <FormControl fullWidth required error={shouldShowValidationMessages && Boolean(formik.errors.priority)}>
                <InputLabel id="feedback-priority-label">Priority</InputLabel>
                <Select
                  labelId="feedback-priority-label"
                  id="priority"
                  name="priority"
                  label="Priority"
                  value={formik.values.priority}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </Select>
                {shouldShowValidationMessages && formik.errors.priority && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.75, ml: 1.75 }}>
                    {formik.errors.priority}
                  </Typography>
                )}
              </FormControl>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="flex-end">
                <Button variant="text" onClick={() => formik.resetForm()} disabled={isSubmittingFeedback || !formik.dirty}>
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmittingFeedback}
                >
                  {isSubmittingFeedback ? "Submitting..." : "Submit Feedback"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Stack>
  );
}

export default withRoleBasedAccess(FeedbackPage, feedbackViewPermissions);
